import * as React from "react";
import { IHeaderParams } from "ag-grid-community";
import { IStore } from "reducers";
import { getCollectionItemDisplayProperty } from "reducers/selectors/collection";
import { add as addSnackbar, SnackbarAddPayload } from "actions/snackbars";
import { connect, Dispatch } from "react-redux";
import { List, Map } from "immutable";
import { ActionOverrideItemDisplayNamePayload, overrideItemDisplayName } from "actions/collections";
import { useRenameableGridHeaderState } from "./use-renameable-grid-header-state";
import { InputWrapper } from "./input-wrapper";
import { LabelIconsWrapper, Sort } from "./labels-icons-wrapper";
import { ISchemaKey } from "helpers/product-collection";

interface IRenameableGridHeaderProps extends IHeaderParams {
    fieldName: string;
}

type StateProps = {
    collectionDisplayNames: Record<string, string>;
    capabilities: nuorder.folder.v1.Capabilities;
};

type DispatchProps = {
    dispatchErrorSnackbar: (params: SnackbarAddPayload) => void;
    overrideItemDisplayName: (payload: ActionOverrideItemDisplayNamePayload) => void;
};

const BaseRenameableGridHeader = (props: IRenameableGridHeaderProps & StateProps & DispatchProps) => {
    const {
        displayName,
        collectionDisplayNames,
        dispatchErrorSnackbar,
        showColumnMenu,
        capabilities,
        column,
        progressSort,
        fieldName,
        overrideItemDisplayName,
    } = props;

    const { inputValue, submitNewValue, onChangeInput, startEditing } = useRenameableGridHeaderState(
        displayName,
        fieldName,
        dispatchErrorSnackbar,
        overrideItemDisplayName
    );

    const canEdit = capabilities.can_edit;
    const isEditing = inputValue === "" ? true : Boolean(inputValue);

    return (
        <div
            className="ag-cell-label-container"
            style={{ flexDirection: "row" }}
            onClick={() => {
                if (!isEditing) {
                    progressSort();
                }
            }}
        >
            {isEditing && canEdit ? (
                <InputWrapper
                    submitNewValue={submitNewValue}
                    collectionDisplayNames={collectionDisplayNames}
                    inputValue={inputValue}
                    onChange={onChangeInput}
                />
            ) : (
                <LabelIconsWrapper
                    canEdit={canEdit}
                    displayName={displayName}
                    isFilterActive={column.isFilterActive()}
                    sorting={column.getSort() as Sort}
                    onClickMenu={showColumnMenu}
                    onEditClickHandler={startEditing}
                />
            )}
        </div>
    );
};

const mapDispatchToProps = (dispatch: Dispatch<{}>): DispatchProps => ({
    dispatchErrorSnackbar: (params) => dispatch(addSnackbar(params)),
    overrideItemDisplayName: (payload) => dispatch(overrideItemDisplayName(payload)),
});

/**
 * This selector maps the List<SchemaKeys> to a Map where the keys are the displayNames to make lookup easy
 */
const itemDisplayPropertyToMapSelector = (store: IStore) => {
    const itemDisplayPropertyList = getCollectionItemDisplayProperty(store, store.collection.collectionId) as List<
        Map<keyof ISchemaKey, string>
    >;

    return (itemDisplayPropertyList ?? List([])).reduce<Record<string, string>>((acc, field) => {
        const displayNameOverride = field.get("display_name_override");
        const displayName =
            field.get("allow_override_display_name") && displayNameOverride
                ? displayNameOverride
                : field.get("display_name");
        acc[displayName] = displayName;
        return acc;
    }, {});
};
const mapStateToProps = (store: IStore): StateProps => ({
    collectionDisplayNames: itemDisplayPropertyToMapSelector(store),
    capabilities: store.collection.driveItem.capabilities,
});
export const RenameableGridHeader = connect(mapStateToProps, mapDispatchToProps)(BaseRenameableGridHeader);
